<template>
  <div class="more-about-yourself-step">

    <!-- Personal Details -->
    <p class="fm-section-label">Personal Details</p>
    <div class="row">
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="First Name" name="firstName" :floating="true">
          <input v-model="form.firstName" class="form-control" name="firstName" type="text" placeholder=" "/>
        </FormGroup>
      </div>
      <div class="col-md-4">
        <FormGroup label="Middle Name" :floating="true">
          <input v-model="form.middleName" class="form-control" name="middleName" type="text" placeholder=" "/>
        </FormGroup>
      </div>
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="Last Name" name="lastName" :floating="true">
          <input v-model="form.lastName" class="form-control" name="lastName" type="text" placeholder=" "/>
        </FormGroup>
      </div>
    </div>

    <div class="row">
      <div class="col-md-4">
        <GenderSelect v-model="form.gender" :error-container="errorContainerName" :floating="true"/>
      </div>
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="Date of Birth" name="dob" :floating="true">
          <calendar-input v-model="form.dob" name="dob"/>
        </FormGroup>
      </div>
      <div class="col-md-4">
        <MaritalStatusSelect v-model="form.maritalStatus" :errorContainer="errorContainerName" :floating="true"/>
      </div>
    </div>

    <div class="row">
      <div class="col-md-4">
        <NumberOfDependentsSelect v-model="form.numberOfDependents" :error-container="errorContainerName" :floating="true"/>
      </div>
      <div class="col-md-8">
        <ResidentialStatusSelect v-model="form.residentialStatus" :error-container="errorContainerName" :floating="true"/>
      </div>
    </div>

    <!-- Residency + Medicare -->
    <div class="fm-info-box">
      <Checkbox v-model="form.australianResident" :error-container="errorContainerName" name="australianResident">
        <template v-slot="{ onClick }">
          <div @click="onClick">I am an Australian Citizen or Permanent Resident of Australia.</div>
        </template>
      </Checkbox>

      <p class="fm-section-label mt-3">Medicare Information</p>
      <div class="row">
        <div class="col-md-6">
          <FormGroup :error-container="errorContainerName" label="Medicare Card Number" name="medicare.number" :floating="true">
            <input v-model="form.medicare.number" class="form-control" name="medicareCardNumber" type="text" placeholder=" "/>
          </FormGroup>
        </div>
        <div class="col-md-6">
          <FormGroup :error-container="errorContainerName" label="Medicare Reference Number" name="medicare.reference" :floating="true">
            <FmSelect :value="form.medicare.reference" :options="medicareReferenceOptions" :icons="medicareRefIcons"
                      @input="form.medicare.reference = $event !== '' ? Number($event) : null"/>
          </FormGroup>
        </div>
        <div class="col-md-4">
          <div class="fm-expiry-group">
            <label id="medicare-color-label" class="fm-expiry-group__label">Card Colour</label>
            <RadioButtonGroup v-model="form.medicare.color" :block="true"
                              :options="{G: 'Green', B: 'Blue', Y: 'Yellow'}"
                              name="medicareCardColor" size="sm" type="outline-secondary"
                              aria-labelledby="medicare-color-label"/>
            <ErrorText :errorContainer="errorContainerName" name="medicare.color"/>
          </div>
        </div>
        <div class="col-md-4">
          <div class="fm-expiry-group">
            <label class="fm-expiry-group__label">Medicare Middle Name</label>
            <input v-model="form.medicare.middleName" class="form-control" name="medicareMiddleName" type="text"/>
          </div>
        </div>
        <div class="col-md-4">
          <div class="fm-expiry-group">
            <label class="fm-expiry-group__label">Medicare Expiry</label>
            <MedicareExpiryDateInput :color="form.medicare.color" v-model="form.medicare.expiry"/>
            <ErrorText :errorContainer="errorContainerName" name="medicare.expiry"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Addresses -->
    <p class="fm-section-label mt-1">Current Address</p>
    <AddressInput v-model="form.addresses.current" :error-container="errorContainerName"
                  error-key-prefix="addresses.current."
                  name="Current Address"/>

    <SmoothReflow :show="isPreviousAddressRequired">
      <AddressInput v-model="form.addresses.previous" :duration="false"
                    :error-container="errorContainerName" error-key-prefix="addresses.previous."
                    name="Previous Address">
        <template v-slot:header-text>
          <div class="fm-alert fm-alert--warning mb-3">
            <svg class="fm-alert__icon" fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
            </svg>
            Previous address required — current address is less than 1 year.
          </div>
        </template>
      </AddressInput>
    </SmoothReflow>

    <!-- ID Verification -->
    <p class="fm-section-label mt-1">Identity Verification</p>
    <IDVerificationSelect v-model="form.idVerification.documentType" :error-container="errorContainerName"
                          name="idVerification.documentType" :floating="true"/>

    <div class="row">
      <div :class="`col-md-${form.idVerification.documentType === 'passport' ? 6 : 4}`">
        <FormGroup v-if="form.idVerification.documentType === 'passport'" :error-container="errorContainerName"
                   label="Passport Number" name="idVerification.passport.number" :floating="true">
          <input v-model="form.idVerification.passport.number" class="form-control" name="documentNumber" type="text" placeholder=" "/>
        </FormGroup>
        <FormGroup v-if="form.idVerification.documentType === 'license'" :error-container="errorContainerName"
                   label="Licence Number" name="idVerification.license.number" :floating="true">
          <input v-model="form.idVerification.license.number" class="form-control" name="documentNumber" type="text" placeholder=" "/>
        </FormGroup>
      </div>
      <div :class="`col-md-${form.idVerification.documentType === 'passport' ? 6 : 4}`">
        <CountrySelect v-if="form.idVerification.documentType === 'passport'"
                       v-model="form.idVerification.passport.country" :error-container="errorContainerName"
                       name="idVerification.passport.country" :floating="true"/>
        <StateSelect v-if="form.idVerification.documentType === 'license'"
                     v-model="form.idVerification.license.state"
                     :error-container="errorContainerName" name="idVerification.license.state" :floating="true"/>
      </div>
      <div v-if="form.idVerification.documentType === 'license'" class="col-md-4">
        <FormGroup :error-container="errorContainerName" name="idVerification.license.cardNumber" :floating="true">
          <template #default="{ id }">
            <input v-model="form.idVerification.license.cardNumber" :id="id" class="form-control"
                   name="licenseCardNumber" type="text" placeholder=" "/>
            <label :for="id" class="col-form-label d-flex align-items-center gap-1">
              Licence Card Number <HelpIcon :selected-state="form.idVerification.license.state"/>
            </label>
          </template>
        </FormGroup>
      </div>
    </div>

    <Checkbox v-model="form.agreeIdCheck" :error-container="errorContainerName" name="agreeIdCheck">
      <template v-slot="{ onClick }">
        <div @click="onClick">
          I agree to have my identity information verified with the Issuer or Official Record Holder.
        </div>
      </template>
    </Checkbox>

    <slot name="controls" v-bind="{ prev: onPrev, next: onNext }"/>

  </div>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';
import RadioButtonGroup from '@modules/shared/components/form/RadioButtonGroup';
import {NEW_APPLICATION} from '@store/constants';
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import {validate} from './formValidator';
import GenderSelect from '@modules/shared/components/user/GenderSelect';
import IDVerificationSelect from '@modules/shared/components/user/IDVerificationSelect';
import StateSelect from '@modules/shared/components/user/StateSelect';
import CountrySelect from '@modules/shared/components/user/CountrySelect';
import Checkbox from '@modules/shared/components/form/FmCheckbox.vue';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import store from '@modules/guest/new-applicant/store/store';
import {saveAsDraft} from '@modules/guest/new-applicant/NewApplicationService';
import {mergeDeep} from '@modules/shared/helpers/object-fns';
import {ApplicationChannel} from '@modules/guest/new-applicant/constants';
import PlacesAutocomplete from '@modules/shared/components/google/AddressAutocomplete';
import AddressInput from '@modules/guest/new-applicant/components/steps/MoreAboutYourself/AddressInput';
import SmoothReflow from '@modules/shared/transitions/SmoothReflow';
import Modal from '@modules/shared/components/modal/Modal';
import ModalBody from '@modules/shared/components/modal/ModalBody';
import ModalHeader from '@modules/shared/components/modal/ModalHeader';
import dayjs from 'dayjs';
import {StatesOfAustralia} from '@modules/guest/bought-lead/constants';
import MedicareExpiryDateInput from '@modules/shared/components/date-input/MedicareExpiryDateInput.vue';
import MaritalStatusSelect from '@modules/shared/components/user/MaritalStatusSelect.vue';
import ResidentialStatusSelect from '@modules/shared/components/user/ResidentialStatusSelect.vue';
import NumberOfDependentsSelect from '@modules/shared/components/user/NumberOfDependentsSelect.vue';
import ErrorText from '@modules/shared/components/form/ErrorText';
import FmSelect from '@modules/shared/components/form/FmSelect.vue';
import CalendarInput from '@modules/shared/components/date-input/CalendarInput.vue';

const ERROR_CONTAINER_NAME = 'NewApplicationMoreAboutYourselfStepForm';

const defaultAddress = () => ({
  unit_number: null, street_number: null, street_name: null, suburb: null,
  state: null, country: null, postcode: null, years: null, months: null,
});

const defaultState = () => ({
  firstName: '', middleName: '', lastName: '', gender: '', dob: '',
  maritalStatus: '', numberOfDependents: '', residentialStatus: '',
  australianResident: null,
  addresses: { current: defaultAddress(), previous: defaultAddress() },
  medicare: { color: 'G', reference: '', middleName: '', expiry: '' },
  idVerification: {
    documentType: '',
    passport: { number: '', country: 'AUS' },
    license: { number: '', state: '', cardNumber: '' },
  },
  agreeIdCheck: '',
});

const HelpIcon = {
  components: { StateSelect, Modal, ModalBody, ModalHeader },
  props: ['selectedState'],
  data() { return { state: null }; },
  watch: {
    selectedState: {
      immediate: true,
      handler(value) { this.state = value ? value : StatesOfAustralia.NSW; },
    },
  },
  computed: {
    image() { return this.state ? `/images/drivers-licences/${this.state}.webp` : null; },
  },
  template: `
    <span>
      <i class="fa fa-question-circle" style="cursor:pointer; color:#9197A1;" @click.prevent="$refs.modal.show"/>
      <Modal ref="modal" :options="{ backdrop: true, keyboard: true }" centered>
        <ModalHeader :dark="false" title="Driver's Licence Help" default-header/>
        <ModalBody class="p-2">
          <StateSelect v-model="state"/>
          <div class="d-flex align-items-center justify-content-center p-1"
               style="border:1px solid #E7E4DA;border-radius:8px;">
            <img :src="image" class="p-0 licence-sample-image" alt="Licence Sample">
          </div>
        </ModalBody>
      </Modal>
    </span>
  `,
};

export default {
  name: 'MoreAboutYourself',
  mixins: [NewApplicationStepMixin],
  components: {
    SmoothReflow, AddressInput, PlacesAutocomplete, RadioButtonGroup, FormGroup,
    GenderSelect, IDVerificationSelect, StateSelect, CountrySelect, Checkbox,
    MaritalStatusSelect, ResidentialStatusSelect, NumberOfDependentsSelect,
    HelpIcon, MedicareExpiryDateInput, ErrorText, FmSelect,
    CalendarInput,
  },
  data() {
    const original = store.state[NEW_APPLICATION.MODULE_NAME].cache ?? {};
    const isTopUp = original?.channel === ApplicationChannel.CUSTOMER_PORTAL_TOP_UP;
    const isReApply = original?.channel === ApplicationChannel.CUSTOMER_PORTAL_RE_APPLY;
    const savedData = original.meta?.MoreAboutYourself ?? {};
    // Only reset agreeIdCheck for fresh applications that have no saved state.
    // Without this guard, mergeDeep would restore the saved `true` value but the
    // override would then immediately trample it back to null, leaving the
    // checkbox unchecked after a page refresh even though the user already agreed.
    const overrides = (isTopUp || isReApply || savedData.agreeIdCheck) ? {} : { agreeIdCheck: null };
    const form = mergeDeep(defaultState(), savedData, overrides);
    return {
      form,
      meta: { showPreviousAddressForm: false },
    };
  },
  computed: {
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    isPreviousAddressRequired() {
      const hasError = Object.keys(this.validationErrors).some(key => key.startsWith('addresses.previous'));
      const years = this.form.addresses.current.years;
      const yearsEntered = years !== null && years !== '' && years !== undefined;
      const yearsInsufficient = yearsEntered && Number(years) < 1;
      return hasError || yearsInsufficient;
    },
    today() { return dayjs().format('YYYY-MM-DD'); },
    medicareReferenceOptions: () => [1, 2, 3, 4, 5, 6, 7, 8, 9],
    // Hash icon for medicare reference number
    medicareRefIcons() {
      const icon = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1zm6 2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V6z"/></svg>`;
      return Object.fromEntries([1,2,3,4,5,6,7,8,9].map(n => [n, icon]));
    },
  },
  watch: {
    form: {
      deep: true, immediate: true,
      handler() { this.shouldGoNextStep(); this.syncWithStore(); },
    },
  },
  methods: {
    validate,
    onNext() {
      if (!this.shouldGoNextStep()) { scrollToErrorFormField(this.$el); return; }
      this.saveAndSync().then(async () => {
        await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
      }).catch(e => { this.handleServerError(e); scrollToErrorFormField(this.$el); });
    },
    onPrev() { store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP); },
    async saveAndSync() {
      const payload = { step: 'MoreAboutYourself', ...this.form };
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, loanNumber, canPerformCS}} = await saveAsDraft(this.code, payload);
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp, loanNumber, canPerformCS}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
      } catch (e) { throw e; }
      finally { store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    syncWithStore() {
      this.$store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_MORE_ABOUT_YOURSELF, payload: { ...this.form } });
    },
    onPreviousAddressRemove() {
      this.meta.showPreviousAddressForm = false;
      this.form.addresses.previous = defaultAddress();
    },
  },
  created() { this.registerErrorContainer(ERROR_CONTAINER_NAME); },
};
</script>

<style lang="scss">
$fm-ink:      #15171C;
$fm-ink-soft: #5B606B;
$fm-line:     #E7E4DA;
$fm-line-soft:#F0EEE7;
$fm-amber:    #FFB000;

// Section label
.fm-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  /* WCAG 1.4.3: was #9197A1 (~2.94:1 on white, fails 4.5:1). #6B7280 clears AA. */
  color: #6B7280;
  margin-bottom: 1rem;
  margin-top: 0;
}

// Boxed info section (Medicare + residency)
.fm-info-box {
  background: #FAFAF8;
  border: 1px solid $fm-line;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 0.5rem;
  margin-bottom: 1.5rem;
}

// Warning alert
.fm-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;

  &__icon { flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; }

  &--warning {
    background: #FFF8E1;
    border: 1px solid rgba(255, 176, 0, 0.3);
    color: #7a5c00;
  }

  &--info {
    background: #EFF6FF;
    border: 1px solid rgba(59, 130, 246, 0.25);
    color: #1d4ed8;
  }
}

.licence-sample-image {
  object-fit: contain;
  width: 100%;
  filter: hue-rotate(180deg) saturate(20%);
}

// Expiry / card-colour / middle-name label wrapper — consistent label-above pattern
.fm-expiry-group {
  margin-bottom: 1.1rem;

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $fm-ink-soft;
    margin-bottom: 6px;
  }

  // Plain input inside an expiry group (label is above, not floating)
  input.form-control {
    height: 56px;
    border-radius: 12px;
    border: 1.5px solid $fm-line;
    font-size: 15px;
    color: $fm-ink;
    background: #fff;
    padding: 0.75rem 0.85rem;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus {
      border-color: $fm-amber;
      box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
      outline: none;
    }
  }
}
</style>
